import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { createPaymentLink } from '../services/payments';

function resolvePlanId(plan) {
  const id = Number(plan?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function usePlanPayment({ onSuccess } = {}) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  const clearPaymentFeedback = useCallback(() => {
    setPaymentError(null);
    setPaymentSuccess(null);
  }, []);

  const startPayment = useCallback(
    async (plan, options = {}) => {
      if (!isLoggedIn) {
        openAuthPopup();
        return false;
      }

      const planId = resolvePlanId(plan);
      if (!planId) {
        setPaymentError('Unable to start payment for this plan. Please refresh and try again.');
        return false;
      }

      setPayingPlanId(planId);
      setPaymentError(null);
      setPaymentSuccess(null);

      try {
        const linkRes = await createPaymentLink({
          planId,
          couponCode: options.couponCode || null,
        });
        const paymentUrl = linkRes?.data?.payment_link_url;
        if (!paymentUrl) {
          throw new Error('Payment link not available. Please try again.');
        }

        window.open(paymentUrl, '_blank', 'noopener,noreferrer');
        setPaymentSuccess('Complete payment in the opened tab to activate your plan.');
        onSuccess?.();
        return true;
      } catch (error) {
        setPaymentError(error?.message || 'Payment failed. Please try again.');
        return false;
      } finally {
        setPayingPlanId(null);
      }
    },
    [isLoggedIn, onSuccess, openAuthPopup]
  );

  return {
    startPayment,
    payingPlanId,
    paymentError,
    paymentSuccess,
    clearPaymentFeedback,
    isLoggedIn,
    openAuthPopup,
  };
}

export function useFoundationPackCheckout({ plans, startPayment, isPlanSubscribed }) {
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === String(selectedPlanId)) || plans[0] || null,
    [plans, selectedPlanId]
  );

  const openPack = useCallback(
    (plan) => {
      if (!isLoggedIn) {
        openAuthPopup();
        return;
      }

      if (!plans?.length) return;

      const preferredId = plan?.id ?? plans[0]?.id ?? null;
      setSelectedPlanId(preferredId);
      setOpen(true);
    },
    [isLoggedIn, openAuthPopup, plans]
  );

  const closePack = useCallback(() => {
    setOpen(false);
  }, []);

  const handleProceed = useCallback(
    async ({ plan, couponCode }) => {
      if (isPlanSubscribed?.(plan)) {
        return false;
      }
      const ok = await startPayment(plan, { couponCode });
      if (ok) setOpen(false);
      return ok;
    },
    [isPlanSubscribed, startPayment]
  );

  useEffect(() => {
    if (!open) return;
    if (!plans.some((plan) => String(plan.id) === String(selectedPlanId))) {
      setSelectedPlanId(plans[0]?.id ?? null);
    }
  }, [open, plans, selectedPlanId]);

  return {
    open,
    selectedPlan,
    selectedPlanId,
    setSelectedPlanId,
    openPack,
    closePack,
    handleProceed,
  };
}
